import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';

/*
 * WYSIWYG editor for `rich_text` PIM attributes (e.g. the marketing
 * Description). The canonical stored value is an HTML string, so this component
 * is value-in / HTML-out: it initialises from the `value` prop and reports
 * edits as HTML via `onChange`. TipTap renders an empty doc as `<p></p>`; we
 * normalise that to '' so completeness/required checks treat it as blank.
 *
 * StarterKit (v3) already bundles the Link mark, so it is configured here rather
 * than registered as a separate extension (which would duplicate it). The server
 * is the security boundary — pasted markup is sanitised on save in
 * `_RichTextField.normalize` (src/waterco/pim/field_types.py).
 */

const TOOLBAR = [
  { id: 'bold', label: 'B', title: 'Bold', style: { fontWeight: 700 } },
  { id: 'italic', label: 'I', title: 'Italic', style: { fontStyle: 'italic' } },
  { id: 'h2', label: 'H2', title: 'Heading' },
  { id: 'h3', label: 'H3', title: 'Subheading' },
  { id: 'bulletList', label: '• List', title: 'Bullet list' },
  { id: 'orderedList', label: '1. List', title: 'Numbered list' },
  { id: 'blockquote', label: '❝', title: 'Quote' },
  { id: 'link', label: '🔗', title: 'Link' },
];

export default function RichTextEditor({ value, onChange, disabled }) {
  // Keep the latest onChange (which closes over the active channel/locale scope)
  // in a ref so the editor's onUpdate never writes to a stale scope after the
  // user switches region/locale without remounting the editor.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        link: {
          openOnClick: false,
          autolink: true,
          HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
        },
      }),
      Placeholder.configure({ placeholder: 'Write the marketing description…' }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChangeRef.current(ed.isEmpty ? '' : ed.getHTML());
    },
  });

  // Re-sync when the value changes externally (scope/locale switch or reload).
  // Guard against clobbering in-progress typing: only replace content when the
  // incoming HTML differs from what the editor already holds.
  useEffect(() => {
    if (!editor) return;
    const incoming = value || '';
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (incoming !== current) {
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) return null;

  const isActive = (id) => {
    if (id === 'h2') return editor.isActive('heading', { level: 2 });
    if (id === 'h3') return editor.isActive('heading', { level: 3 });
    return editor.isActive(id);
  };

  const run = (id) => {
    const chain = editor.chain().focus();
    switch (id) {
      case 'bold': return chain.toggleBold().run();
      case 'italic': return chain.toggleItalic().run();
      case 'h2': return chain.toggleHeading({ level: 2 }).run();
      case 'h3': return chain.toggleHeading({ level: 3 }).run();
      case 'bulletList': return chain.toggleBulletList().run();
      case 'orderedList': return chain.toggleOrderedList().run();
      case 'blockquote': return chain.toggleBlockquote().run();
      case 'link': return setLink();
      default: return undefined;
    }
  };

  const setLink = () => {
    const prev = editor.getAttributes('link').href || '';
    // eslint-disable-next-line no-alert
    const url = window.prompt('Link URL (leave blank to remove)', prev);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div className={`pim-rte ${disabled ? 'is-disabled' : ''}`}>
      {!disabled && (
        <div className="pim-rte-toolbar">
          {TOOLBAR.map((b) => (
            <button
              key={b.id}
              type="button"
              title={b.title}
              style={b.style}
              className={isActive(b.id) ? 'active' : ''}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => run(b.id)}
            >
              {b.label}
            </button>
          ))}
          <span className="pim-rte-sep" />
          <button
            type="button"
            title="Undo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
          >
            ↶
          </button>
          <button
            type="button"
            title="Redo"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
          >
            ↷
          </button>
        </div>
      )}
      <EditorContent editor={editor} className="pim-rte-content" />
    </div>
  );
}
